import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import API_BASE_URL from "../config";

// Every upload is scoped to one event + one folder within that event (folders
// are purely organizational — "General" is folderId === null/undefined).
// This key is how concurrent uploads to different folders of the same event
// stay independent in state, localStorage, and IndexedDB.
export function makeUploadKey(eventId, folderId) {
  return `${eventId}_${folderId == null ? "general" : folderId}`;
}

// The upload-job endpoints (create/add-images/finalize/cancel/retry) require the caller's
// session token so the server can verify they actually own the event being uploaded to.
//
// A co-worker uploading through a link + PIN (UploadLinkPage) has no studio session;
// instead startUpload is given a link session token, kept per upload key in localStorage
// so a page refresh can resume the job with the same credentials. That override wins
// over the studio token so a logged-in studio opening a link still uploads as the link.
const uploadAuthKey = (key) => `upload_job_auth_${key}`;

function authHeader(key) {
  const override = key ? localStorage.getItem(uploadAuthKey(key)) : null;
  return { Authorization: `Bearer ${override || localStorage.getItem("token")}` };
}

function clearUploadAuth(key) {
  localStorage.removeItem(uploadAuthKey(key));
}

// === IndexedDB helpers for resumable uploads (shared across events/folders) ===
const DB_NAME = "photentra_uploads";
// v2: the pending-file index moved from single "eventId" scoping to a
// compound "groupKey" (event + folder) so per-folder uploads don't collide.
const DB_VERSION = 2;
const STORE_NAME = "pending_files";

function openUploadDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME);
      }
      const store = db.createObjectStore(STORE_NAME, { keyPath: "key" });
      store.createIndex("groupKey", "groupKey", { unique: false });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function storeFilesForUpload(groupKey, files) {
  const prepared = await Promise.all(files.map(async (file) => ({
    key: `${groupKey}_${file.name}`,
    groupKey,
    filename: file.name,
    data: await file.arrayBuffer(),
    type: file.type,
    uploaded: false,
  })));

  const db = await openUploadDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  for (const item of prepared) {
    store.put(item);
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function markFileUploaded(groupKey, filename) {
  const db = await openUploadDB();
  const key = `${groupKey}_${filename}`;

  return new Promise((resolve, reject) => {
    const readTx = db.transaction(STORE_NAME, "readonly");
    const readStore = readTx.objectStore(STORE_NAME);
    const readReq = readStore.get(key);

    readReq.onsuccess = () => {
      const existing = readReq.result;
      if (!existing) {
        db.close();
        resolve();
        return;
      }

      const writeTx = db.transaction(STORE_NAME, "readwrite");
      const writeStore = writeTx.objectStore(STORE_NAME);
      writeStore.put({ ...existing, uploaded: true });

      writeTx.oncomplete = () => { db.close(); resolve(); };
      writeTx.onerror = () => { db.close(); reject(writeTx.error); };
    };

    readReq.onerror = () => { db.close(); reject(readReq.error); };
    readTx.onerror = () => { db.close(); reject(readTx.error); };
  });
}

async function getPendingFiles(groupKey) {
  const db = await openUploadDB();
  const tx = db.transaction(STORE_NAME, "readonly");
  const store = tx.objectStore(STORE_NAME);
  const index = store.index("groupKey");
  return new Promise((resolve, reject) => {
    const req = index.getAll(groupKey);
    req.onsuccess = () => {
      db.close();
      const pending = req.result.filter((f) => !f.uploaded);
      resolve(pending);
    };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

async function getAllStoredFiles(groupKey) {
  const db = await openUploadDB();
  const tx = db.transaction(STORE_NAME, "readonly");
  const store = tx.objectStore(STORE_NAME);
  const index = store.index("groupKey");
  return new Promise((resolve, reject) => {
    const req = index.getAll(groupKey);
    req.onsuccess = () => { db.close(); resolve(req.result); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

async function clearUploadFiles(groupKey) {
  const db = await openUploadDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  const index = store.index("groupKey");
  const req = index.getAllKeys(groupKey);
  return new Promise((resolve, reject) => {
    req.onsuccess = () => {
      for (const key of req.result) store.delete(key);
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

// === Client-side image compression ===
const MAX_DIMENSION = 2048;
const JPEG_QUALITY = 0.82;
// Pro Max compressed mode: lighter compression targeting ~1–2 MB
const MAX_DIMENSION_PROMAX = 3840;
const JPEG_QUALITY_PROMAX = 0.85;

const RAW_EXTENSIONS = new Set([
  '.cr2', '.cr3', '.nef', '.arw', '.dng', '.orf', '.raf', '.rw2',
  '.pef', '.srw', '.x3f', '.3fr', '.mrw', '.nrw', '.rwl',
]);
const isRawFile = (name) => {
  if (!name) return false;
  const dot = name.lastIndexOf('.');
  return dot !== -1 && RAW_EXTENSIONS.has(name.slice(dot).toLowerCase());
};
const RAW_MAX_SIZE = 150 * 1024 * 1024; // 150 MB per RAW file

// Extract the embedded full-resolution JPEG from a RAW camera file by scanning
// for JPEG markers (FFD8 start, FFD9 end). Picks the largest segment > 100KB
// that starts with a valid JPEG image marker (APP/DQT/SOF), filtering out
// JPEG-compressed sensor data fragments (DHT/SOS without preamble).
async function extractJpegFromRaw(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer);
  let best = null;
  for (let i = 0; i < bytes.length - 3; i++) {
    if (bytes[i] === 0xFF && bytes[i + 1] === 0xD8) {
      if (bytes[i + 2] !== 0xFF) { i += 1; continue; }
      const m = bytes[i + 3];
      const isApp = m >= 0xE0 && m <= 0xEF;
      const isDqt = m === 0xDB;
      const isSof = m >= 0xC0 && m <= 0xC3;
      const isCom = m === 0xFE;
      if (!isApp && !isDqt && !isSof && !isCom) continue;
      for (let j = i + 2; j < bytes.length - 1; j++) {
        if (bytes[j] === 0xFF && bytes[j + 1] === 0xD9) {
          const segment = bytes.subarray(i, j + 2);
          if (!best || segment.length > best.length) {
            best = segment;
          }
          break;
        }
      }
    }
  }
  if (!best || best.length < 100000) return null;
  const blob = new Blob([best], { type: 'image/jpeg' });
  return blob;
}

// Read the TIFF IFD0 Orientation tag (0x0112) straight out of the RAW container.
// Cameras record a portrait frame as landscape sensor pixels plus this flag, and
// the embedded preview usually carries no EXIF orientation of its own — so
// without this a portrait shot uploads on its side. Returns an EXIF orientation
// value (1-8), or 0 when the format isn't TIFF-based (CR3, RAF) or has no tag.
function getRawOrientation(arrayBuffer) {
  try {
    const dv = new DataView(arrayBuffer);
    if (dv.byteLength < 16) return 0;
    const bom = dv.getUint16(0, false);
    let le;
    if (bom === 0x4949) le = true;
    else if (bom === 0x4D4D) le = false;
    else return 0;
    const ifdOff = dv.getUint32(4, le);
    if (ifdOff <= 0 || ifdOff > dv.byteLength - 2) return 0;
    const count = dv.getUint16(ifdOff, le);
    if (count > 512) return 0;
    for (let i = 0; i < count; i++) {
      const entry = ifdOff + 2 + i * 12;
      if (entry + 12 > dv.byteLength) break;
      if (dv.getUint16(entry, le) === 0x0112) {
        const v = dv.getUint16(entry + 8, le);
        return v >= 1 && v <= 8 ? v : 0;
      }
    }
  } catch (e) { /* not TIFF-based; leave orientation unknown */ }
  return 0;
}

const UPLOAD_CONCURRENCY = 3;
// RAW originals are ~35MB each; two in flight saturates a typical uplink without
// starving the preview uploads that the gallery is actually waiting on.
const RAW_BACKUP_CONCURRENCY = 2;
// A dropped backup is recoverable but only by re-selecting the file, so retry
// transient failures a few times before giving up and flagging it.
const RAW_BACKUP_RETRIES = 3;
const SLEEP_MS = 300;
const MAX_UPLOAD_RETRIES = 3;
// How long a fully-successful upload stays visible in the widget before it clears itself.
const AUTO_CLEAR_MS = 4000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Send originals for photos already in the gallery whose backup was interrupted.
// No upload job, no preview work, no quota charge — the photo already exists and
// only its RAW is missing. Used when a re-dragged folder contains nothing new.
async function uploadRawRepairs(key, repairs, onProgress) {
  let done = 0;
  let failed = 0;
  let next = 0;
  const runner = async () => {
    while (next < repairs.length) {
      const rep = repairs[next];
      next += 1;
      let ok = false;
      for (let attempt = 0; attempt <= RAW_BACKUP_RETRIES && !ok; attempt++) {
        if (attempt > 0) await wait(2000 * attempt);
        try {
          const res = await fetch(`${API_BASE_URL}/api/images/${rep.imageId}/raw-upload`, {
            method: "POST",
            headers: { "Content-Type": "application/octet-stream", "X-Raw-Filename": rep.file.name, ...authHeader(key) },
            body: rep.file,
          });
          ok = res.ok;
        } catch (e) { ok = false; }
      }
      done += 1;
      if (!ok) failed += 1;
      onProgress(done, repairs.length, failed);
    }
  };
  await Promise.all(Array.from({ length: Math.min(RAW_BACKUP_CONCURRENCY, repairs.length) }, runner));
  return { done, failed };
}

async function fetchWithRetry(url, options, retries = MAX_UPLOAD_RETRIES) {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, options);
      if (response.ok || response.status === 400 || response.status === 404) return response;
      if (attempt < retries) {
        await wait(attempt * 2000);
        continue;
      }
      return response;
    } catch (err) {
      if (attempt < retries) {
        await wait(attempt * 2000);
        continue;
      }
      throw err;
    }
  }
}

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function storedFileToDataURL(file) {
  const uint8 = new Uint8Array(file.data);
  let binary = "";
  const chunkSize = 8192;
  for (let j = 0; j < uint8.length; j += chunkSize) {
    binary += String.fromCharCode(...uint8.subarray(j, j + chunkSize));
  }
  return `data:${file.type || "image/jpeg"};base64,${btoa(binary)}`;
}

function compressImage(file, proMax = false) {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image/")) return resolve(file);

    const maxDim = proMax ? MAX_DIMENSION_PROMAX : MAX_DIMENSION;
    const quality = proMax ? JPEG_QUALITY_PROMAX : JPEG_QUALITY;

    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;

      if (width <= maxDim && height <= maxDim) {
        // Still re-encode to JPEG for consistent compression
      } else if (width > height) {
        height = Math.round(height * (maxDim / width));
        width = maxDim;
      } else {
        width = Math.round(width * (maxDim / height));
        height = maxDim;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) return resolve(file);
          const compressed = new File(
            [blob],
            file.name.replace(/\.[^.]+$/, ".jpg"),
            { type: "image/jpeg" }
          );
          resolve(compressed);
        },
        "image/jpeg",
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
}

function mergeImages(existing, incoming) {
  const existingIds = new Set(existing.map((img) => img.id));
  const fresh = incoming.filter((img) => !existingIds.has(img.id));
  return fresh.length > 0 ? [...existing, ...fresh] : existing;
}

// Appends "• 142/200 photos used" when the plan has a finite per-event limit.
function quotaSuffix(quotaBaseline, quotaMax, extra) {
  if (quotaMax == null) return "";
  return ` • ${quotaBaseline + extra}/${quotaMax} photos used`;
}

const UploadManagerContext = createContext(null);

export function useUploadManager() {
  return useContext(UploadManagerContext);
}

export function UploadManagerProvider({ children }) {
  const [uploads, setUploads] = useState({}); // key -> upload state
  // Per-upload mutable bits that shouldn't trigger re-renders on their own.
  const refsRef = useRef({}); // key -> { cancelled, paused, uploadingFresh, pollTimer }

  const getRefs = (key) => {
    if (!refsRef.current[key]) {
      refsRef.current[key] = { cancelled: false, paused: false, uploadingFresh: false, pollTimer: null };
    }
    return refsRef.current[key];
  };

  const patchUpload = useCallback((key, patch) => {
    setUploads((prev) => {
      const current = prev[key] || {};
      const next = typeof patch === "function" ? patch(current) : patch;
      return { ...prev, [key]: { ...current, ...next } };
    });
  }, []);

  const removeUpload = useCallback((key) => {
    setUploads((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const dismissUpload = useCallback((eventId, folderId) => {
    const key = makeUploadKey(eventId, folderId);
    const r = refsRef.current[key];
    if (r?.pollTimer) clearTimeout(r.pollTimer);
    removeUpload(key);
  }, [removeUpload]);

  const pollJobStatus = useCallback((key, jobId) => {
    const run = async () => {
      try {
        const r0 = getRefs(key);
        // Cursor over completed job items — without it a 1000-photo job re-sends
        // (and re-signs URLs for) the whole completed set on every poll. Keyed on
        // completion order rather than item id: workers finish out of order, so a
        // highest-id cursor skips any item that lands late with a lower id.
        const since = r0.lastCompletionSeq || 0;
        const response = await fetch(`${API_BASE_URL}/api/upload-jobs/${jobId}?sinceSeq=${since}`);
        if (!response.ok) {
          patchUpload(key, { isUploading: false, jobId: null });
          localStorage.removeItem(`upload_job_${key}`);
      clearUploadAuth(key);
          return;
        }
        const data = await response.json();
        const { job, completedImages } = data;
        const r = getRefs(key);
        if (completedImages && completedImages.length > 0) {
          r.lastCompletionSeq = completedImages.reduce((m, img) => Math.max(m, img.completionSeq || 0), since);
        }

        const progress = r.uploadingFresh ? 50 + Math.round(job.progress / 2) : job.progress;

        patchUpload(key, (prev) => ({
          progress,
          statusText: `Stage 2/2 • AI face scan in progress... ${job.completed_images + job.failed_images}/${job.total_images}`
            + quotaSuffix(prev.quotaBaseline || 0, prev.quotaMax, job.completed_images),
          completedImages: completedImages && completedImages.length > 0
            ? mergeImages(prev.completedImages || [], completedImages)
            : (prev.completedImages || []),
        }));

        if (job.status === "completed" || job.status === "failed" || job.status === "cancelled") {
          r.uploadingFresh = false;
          r.cancelled = false;
          localStorage.removeItem(`upload_job_${key}`);
      clearUploadAuth(key);
          clearUploadFiles(key).catch(() => {});

          let success = "", error = "", failedCount = 0, lastFinishedJobId = null;
          if (job.status === "cancelled") {
            success = `Upload cancelled. ${job.completed_images} photos were already processed.`;
          } else if (job.failed_images > 0) {
            failedCount = job.failed_images;
            lastFinishedJobId = jobId;
            error = `Upload finished: ${job.completed_images} succeeded, ${job.failed_images} failed. You can retry the failed uploads.`;
          } else if (job.status === "completed") {
            success = `All ${job.completed_images} photos uploaded successfully!`;
          } else {
            error = "Upload failed. Please try again.";
          }

          patchUpload(key, (prev) => {
            const skipNote = prev.alreadyUploadedCount > 0
              ? ` (${prev.alreadyUploadedCount} already uploaded, skipped)`
              : "";
            return {
              isUploading: false,
              jobId: null,
              progress: job.status === "cancelled" ? 0 : 100,
              success: success ? success + skipNote : success,
              error,
              failedCount,
              lastFinishedJobId,
              statusText: "",
            };
          });

          if (job.failed_images === 0) {
            // Hold the widget open while RAW originals are still uploading in
            // the background — clearing it would hide the only indication that
            // closing the tab right now would abandon a backup.
            const clearWhenIdle = () => {
              const r2 = refsRef.current[key];
              if (r2 && r2.rawPending > 0) {
                setTimeout(clearWhenIdle, 1000);
                return;
              }
              if (r2?.pollTimer) clearTimeout(r2.pollTimer);
              removeUpload(key);
            };
            setTimeout(clearWhenIdle, AUTO_CLEAR_MS);
          }
          return;
        }

        r.pollTimer = setTimeout(run, 2000);
      } catch (err) {
        getRefs(key).pollTimer = setTimeout(run, 5000);
      }
    };
    run();
  }, [patchUpload, removeUpload]);

  const resumeUpload = useCallback(async (key, jobId) => {
    const r = getRefs(key);
    try {
      const pendingFiles = await getPendingFiles(key);

      if (pendingFiles.length > 0) {
        patchUpload(key, { statusText: `Stage 1/2 • Resuming upload of ${pendingFiles.length} remaining photos...`, isUploading: true });
        r.uploadingFresh = true;

        const allFiles = await getAllStoredFiles(key);
        const totalFiles = allFiles.length;

        let nextIndex = 0;
        let completedCount = 0;
        const alreadyUploaded = totalFiles - pendingFiles.length;
        const workerCount = Math.min(UPLOAD_CONCURRENCY, pendingFiles.length);

        const resumeWorker = async () => {
          while (!r.cancelled) {
            while (r.paused && !r.cancelled) {
              await wait(SLEEP_MS);
            }
            if (r.cancelled) return;

            const i = nextIndex;
            nextIndex += 1;
            if (i >= pendingFiles.length) return;

            const file = pendingFiles[i];
            const uploadedSoFar = alreadyUploaded + completedCount;
            patchUpload(key, { statusText: `Stage 1/2 • Uploading ${uploadedSoFar + 1}/${totalFiles} photos...` });

            try {
              const base64 = storedFileToDataURL(file);
              const bodyObj = { imageData: base64, filename: file.filename };
              const imgResponse = await fetchWithRetry(
                `${API_BASE_URL}/api/upload-jobs/${jobId}/images`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json", ...authHeader(key) },
                  body: JSON.stringify(bodyObj),
                }
              );
              if (imgResponse.ok) {
                await markFileUploaded(key, file.filename);
              }
            } catch (err) {
              // best-effort; job finalize below reconciles the actual count
            } finally {
              completedCount += 1;
              const totalUploaded = alreadyUploaded + completedCount;
              patchUpload(key, { progress: Math.round((totalUploaded / totalFiles) * 50) });
            }
          }
        };

        await Promise.all(Array.from({ length: workerCount }, () => resumeWorker()));

        if (r.cancelled) return;

        await fetch(`${API_BASE_URL}/api/upload-jobs/${jobId}/finalize`, { method: "PATCH", headers: authHeader(key) });
        patchUpload(key, { statusText: "Stage 2/2 • AI face scan in progress...", progress: 50 });
      } else {
        r.uploadingFresh = false;
        patchUpload(key, { statusText: "Stage 2/2 • AI face scan in progress...", isUploading: true });
        await fetch(`${API_BASE_URL}/api/upload-jobs/${jobId}/finalize`, { method: "PATCH", headers: authHeader(key) }).catch(() => {});
      }

      pollJobStatus(key, jobId);
    } catch (err) {
      r.uploadingFresh = false;
      patchUpload(key, { statusText: "Stage 2/2 • AI face scan in progress...", isUploading: true });
      await fetch(`${API_BASE_URL}/api/upload-jobs/${jobId}/finalize`, { method: "PATCH", headers: authHeader(key) }).catch(() => {});
      pollJobStatus(key, jobId);
    }
  }, [patchUpload, pollJobStatus]);

  // On app start, resume any uploads that were interrupted by a full page reload —
  // across every event and folder, not just whichever one the user happens to be viewing.
  // RAW originals finish uploading after the gallery is already usable, so the
  // tab can look idle while a 35MB backup is still in flight. Warn before that
  // gets thrown away — this is the safety net for backgrounding the backup.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      const pending = Object.values(refsRef.current).reduce((n, r) => n + (r?.rawPending || 0), 0);
      if (pending > 0) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
      return undefined;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  useEffect(() => {
    const hasStudioSession = !!localStorage.getItem("token");
    const jobKeyRe = /^upload_job_(\d+)_(.+)$/;
    for (let i = 0; i < localStorage.length; i++) {
      const storageKey = localStorage.key(i);
      const match = storageKey && storageKey.match(jobKeyRe);
      if (!match) continue;
      const eventId = match[1];
      const folderKey = match[2];
      const key = `${eventId}_${folderKey}`;
      // A co-worker link upload carries its own credentials; anything else needs a login.
      if (!hasStudioSession && !localStorage.getItem(uploadAuthKey(key))) continue;
      const jobId = parseInt(localStorage.getItem(storageKey), 10);
      if (!jobId) continue;

      const folderId = folderKey === "general" ? null : folderKey;
      patchUpload(key, {
        eventId, folderId, jobId, isUploading: true, progress: 0,
        statusText: "Resuming upload...", error: "", success: "", failedCount: 0,
      });
      getRefs(key).cancelled = false;
      getRefs(key).paused = false;

      // Best-effort event/folder name for the widget label.
      fetch(`${API_BASE_URL}/api/events/single/${eventId}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => { if (data?.event) patchUpload(key, { eventName: data.event.event_name }); })
        .catch(() => {});
      if (folderId) {
        fetch(`${API_BASE_URL}/api/events/${eventId}/folders`)
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            const folder = data?.folders?.find((f) => String(f.id) === String(folderId));
            if (folder) patchUpload(key, { folderLabel: folder.name });
          })
          .catch(() => {});
      }

      resumeUpload(key, jobId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startUpload = useCallback(async (eventId, eventName, files, folderId = null, folderLabel = null, skipCompression = false, proMaxCompressed = false, authToken = null) => {
    const key = makeUploadKey(eventId, folderId);
    if (authToken) localStorage.setItem(uploadAuthKey(key), authToken);
    else clearUploadAuth(key);
    const sizeOkFiles = [];
    const skippedNames = [];
    for (let i = 0; i < files.length; i++) {
      const maxSize = isRawFile(files[i].name) ? RAW_MAX_SIZE : 50 * 1024 * 1024;
      if (files[i].size > maxSize) {
        skippedNames.push(files[i].name);
        continue;
      }
      sizeOkFiles.push(files[i]);
    }

    if (sizeOkFiles.length === 0) {
      if (skippedNames.length > 0) {
        const limitLabel = isRawFile(skippedNames[0]) ? "150MB" : "50MB";
        patchUpload(key, { eventId, folderId, eventName, folderLabel, error: `${skippedNames[0]} is too large (max ${limitLabel}), skipping` });
      }
      return;
    }

    const r = getRefs(key);
    r.cancelled = false;
    r.paused = false;

    patchUpload(key, {
      eventId,
      folderId,
      eventName,
      folderLabel,
      isUploading: true,
      isPaused: false,
      progress: 0,
      statusText: `Preparing • Checking ${sizeOkFiles.length} photos against what's already uploaded...`,
      error: skippedNames.length > 0 ? `${skippedNames[0]} is too large (max 50MB), skipping` : "",
      success: "",
      failedCount: 0,
      lastFinishedJobId: null,
      alreadyUploadedCount: 0,
      completedImages: [],
      quotaBaseline: 0,
      quotaMax: null,
    });

    // Re-selecting a whole local folder (e.g. a photographer copying their
    // full camera dump after an event) should skip whatever's already on the
    // server instead of re-compressing/re-uploading it — checked by filename
    // up front so it's fast, not left to the backend's per-item dupe check.
    let alreadyUploadedCount = 0;
    let validFiles = sizeOkFiles;
    // Photos already in the gallery whose RAW original never made it. Dragging
    // the same folder in again is the natural way a photographer retries, so
    // treat those as repairs instead of skipping them as duplicates: the JPEG
    // exists, so there's nothing to extract or compress — just send the
    // original. Costs one request and none of the prep work.
    let rawRepairs = [];
    try {
      const folderParam = folderId == null ? "general" : folderId;
      const [namesRes, missingRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/events/${eventId}/images/filenames?folderId=${folderParam}`),
        fetch(`${API_BASE_URL}/api/events/${eventId}/missing-raw-backups`, { headers: authHeader(key) }).catch(() => null),
      ]);

      const missingByName = new Map();
      if (missingRes && missingRes.ok) {
        const missingData = await missingRes.json();
        for (const m of missingData.missing || []) {
          if (m.original_filename) missingByName.set(m.original_filename.toLowerCase(), m.id);
        }
      }

      if (namesRes.ok) {
        const namesData = await namesRes.json();
        const existing = new Set(namesData.filenames || []);
        const fresh = [];
        for (const f of sizeOkFiles) {
          if (!existing.has(f.name)) { fresh.push(f); continue; }
          const imageId = missingByName.get(f.name.toLowerCase());
          if (imageId && isRawFile(f.name)) rawRepairs.push({ imageId, file: f });
          else alreadyUploadedCount += 1;
        }
        validFiles = fresh;
      } else if (missingByName.size > 0) {
        // Name list unavailable but we know which originals are missing.
        for (const f of sizeOkFiles) {
          const imageId = missingByName.get(f.name.toLowerCase());
          if (imageId && isRawFile(f.name)) rawRepairs.push({ imageId, file: f });
        }
      }
    } catch (err) {
      // Couldn't check (offline etc) — fall back to uploading everything;
      // the backend's own per-filename dupe check still protects against
      // actually storing duplicates, this pre-check is purely an optimization.
    }

    if (validFiles.length === 0 && rawRepairs.length === 0) {
      patchUpload(key, {
        isUploading: false,
        progress: 0,
        statusText: "",
        success: `All ${alreadyUploadedCount} photo${alreadyUploadedCount > 1 ? "s" : ""} already uploaded — nothing new to add.`,
      });
      return;
    }

    // Nothing new to add, but some originals are missing: repair without
    // creating an upload job at all — no previews to build, no photo quota to
    // charge, just the originals going up.
    if (validFiles.length === 0) {
      patchUpload(key, {
        alreadyUploadedCount,
        statusText: `Restoring ${rawRepairs.length} missing RAW original${rawRepairs.length > 1 ? "s" : ""}...`,
      });
      r.rawPending = rawRepairs.length;
      const { done, failed } = await uploadRawRepairs(key, rawRepairs, (n, total) => {
        r.rawPending = total - n;
        patchUpload(key, {
          progress: Math.round((n / total) * 100),
          statusText: `Restoring RAW originals ${n}/${total}...`,
        });
      });
      r.rawPending = 0;
      patchUpload(key, {
        isUploading: false,
        progress: 0,
        statusText: "",
        success: failed > 0
          ? `Restored ${done - failed} of ${done} RAW originals — ${failed} still missing, try again.`
          : `Restored ${done} missing RAW original${done > 1 ? "s" : ""}.`,
      });
      return;
    }

    patchUpload(key, { statusText: skipCompression ? `Preparing • Reading ${validFiles.length} photos...` : `Preparing • Compressing ${validFiles.length} photos...`, alreadyUploadedCount });

    try {
      // Each file is prepared (RAW preview extraction / compression) inside the
      // upload worker rather than in one pass up front. Preparing everything
      // first meant nothing uploaded until the slowest file had been read,
      // scanned and re-encoded — a long dead gap before the bar moved — and it
      // held every RAW buffer in memory at once (20 files x 34MB thrashes GC).
      // Pipelined this way, only UPLOAD_CONCURRENCY RAWs are resident at a time
      // and each is released as soon as its backup lands.
      const prepareFile = async (file) => {
        if (isRawFile(file.name)) {
          const rawArrayBuffer = await file.arrayBuffer();
          const jpegBlob = await extractJpegFromRaw(rawArrayBuffer);
          if (!jpegBlob) return { file, extracted: false, orientation: 0 };
          const orientation = getRawOrientation(rawArrayBuffer);
          // The embedded preview is already a full-resolution JPEG the camera
          // encoded from sensor data. Putting it through compressImage() again
          // costs a canvas decode/resize/re-encode of a ~6000x4000 image and
          // returns a visibly worse picture for the trouble, so ship it as-is.
          // The ArrayBuffer is dropped here — the backup below streams the
          // original File straight from disk rather than holding 35MB per photo.
          return {
            file: new File([jpegBlob], file.name, { type: 'image/jpeg' }),
            extracted: true,
            orientation,
          };
        }
        const compressed = skipCompression ? file : await compressImage(file, proMaxCompressed);
        return { file: compressed, extracted: false, orientation: 0 };
      };

      const jobResponse = await fetch(
        `${API_BASE_URL}/api/events/${eventId}/upload-job`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeader(key) },
          body: JSON.stringify({ totalImages: validFiles.length, folderId }),
        }
      );

      if (jobResponse.status === 402) {
        // Subscription expired (grace or locked): not a transient failure, so
        // don't dress it up as "Upload failed". Flag it so the widget shows a
        // Recharge Wallet button instead of a retry.
        const errData = await jobResponse.json().catch(() => ({}));
        patchUpload(key, {
          isUploading: false, progress: 0, statusText: "", failedCount: 0,
          subscriptionPaused: true,
          error: errData.message || "Uploads are paused — your subscription has expired.",
        });
        return;
      }
      if (!jobResponse.ok) {
        const errData = await jobResponse.json();
        throw new Error(errData.message || "Failed to create upload job");
      }

      const jobData = await jobResponse.json();
      const jobId = jobData.job.id;
      const quotaBaseline = jobData.quota?.current ?? 0;
      const quotaMax = jobData.quota?.max ?? null;

      localStorage.setItem(`upload_job_${key}`, jobId.toString());
      patchUpload(key, { jobId, quotaBaseline, quotaMax });
      r.uploadingFresh = true;

      let uploadedCount = 0;
      let nextIndex = 0;
      let completedCount = 0;
      const workerCount = Math.min(UPLOAD_CONCURRENCY, validFiles.length);

      // RAW originals back up in the background so the gallery is usable as soon
      // as the previews land — the 35MB-per-photo transfer was ~60% of the wall
      // clock. Each job carries the original File, not its bytes: fetch streams
      // it from disk, so a queue of 50 pending backups still costs no memory.
      // r.rawPending is what the beforeunload guard and the widget watch.
      const rawQueue = rawRepairs.map((rep) => ({
        url: `${API_BASE_URL}/api/images/${rep.imageId}/raw-upload`,
        file: rep.file,
        attempt: 0,
      }));
      let rawActive = 0;
      r.rawTotal = rawQueue.length;
      r.rawDone = 0;
      r.rawPending = rawQueue.length;
      r.rawFailed = 0;
      let onRawDrained = null;
      const rawDrained = new Promise((resolve) => { onRawDrained = resolve; });

      const patchRawStatus = () => {
        if (r.rawPending > 0) {
          patchUpload(key, { rawBackupText: `Backing up ${r.rawDone + 1}/${r.rawTotal} RAW originals — safe to keep working, don't close this tab` });
        } else if (r.rawFailed > 0) {
          patchUpload(key, { rawBackupText: `${r.rawFailed} RAW original${r.rawFailed > 1 ? "s" : ""} failed to back up — use "Restore RAW backups" on the event to finish` });
        } else {
          patchUpload(key, { rawBackupText: "" });
        }
      };

      const pumpRawQueue = () => {
        while (rawActive < RAW_BACKUP_CONCURRENCY && rawQueue.length > 0 && !r.cancelled) {
          const job = rawQueue.shift();
          rawActive += 1;
          const settle = (ok) => {
            rawActive -= 1;
            if (!ok && job.attempt < RAW_BACKUP_RETRIES) {
              // Transient drop (wifi blip, sleep, 5xx) — requeue with backoff
              // rather than losing the original. The File is disk-backed, so
              // holding it across the delay costs nothing.
              job.attempt += 1;
              setTimeout(() => { rawQueue.push(job); pumpRawQueue(); }, 2000 * job.attempt);
              return;
            }
            r.rawDone += 1;
            r.rawPending -= 1;
            if (!ok) r.rawFailed = (r.rawFailed || 0) + 1;
            patchRawStatus();
            pumpRawQueue();
            if (r.rawPending === 0 && rawQueue.length === 0 && onRawDrained) onRawDrained();
          };
          fetch(job.url, {
            method: "POST",
            headers: { "Content-Type": "application/octet-stream", "X-Raw-Filename": job.file.name, ...authHeader(key) },
            body: job.file,
          })
            .then((res) => settle(res.ok))
            .catch(() => settle(false));
        }
      };

      const uploadWorker = async () => {
        while (!r.cancelled) {
          while (r.paused && !r.cancelled) {
            await wait(SLEEP_MS);
          }
          if (r.cancelled) return;

          const i = nextIndex;
          nextIndex += 1;
          if (i >= validFiles.length) return;

          const current = completedCount + 1;
          const isRaw = isRawFile(validFiles[i].name);
          patchUpload(key, { statusText: `Stage 1/2 • ${isRaw ? "Extracting + uploading" : "Uploading"} ${current}/${validFiles.length} photos...` + quotaSuffix(quotaBaseline, quotaMax, current - 1) });

          let prepared = null;
          try {
            prepared = await prepareFile(validFiles[i]);
            if (r.cancelled) return;

            // IndexedDB is the resume store for ordinary photos, whose bytes are
            // otherwise unrecoverable after a reload. A RAW-extracted preview is
            // different: it can be rebuilt from the file on disk, and re-dragging
            // the folder now repairs both the preview and the original. Caching
            // them would mean ~3GB in IndexedDB for a 1000-photo card — well past
            // the browser quota, which would fail the upload outright.
            if (!prepared.extracted) {
              try {
                await storeFilesForUpload(key, [prepared.file]);
              } catch (dbErr) {
                // Out of quota or private-browsing: resume is lost, but the
                // upload itself can still proceed.
              }
            }

            const base64 = await fileToDataURL(prepared.file);
            if (r.cancelled) return;

            const bodyObj = { imageData: base64, filename: prepared.file.name, clientExtracted: !!prepared.extracted, rawOrientation: prepared.orientation || 0 };

            const imgResponse = await fetchWithRetry(
              `${API_BASE_URL}/api/upload-jobs/${jobId}/images`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader(key) },
                body: JSON.stringify(bodyObj),
              }
            );

            if (imgResponse.ok) {
              const imgData = await imgResponse.json();
              if (!imgData.skipped) uploadedCount++;
              await markFileUploaded(key, prepared.file.name);

              // Queued rather than awaited: the preview is already stored, so
              // the gallery need not wait on a 35MB transfer. The tab-close
              // guard below covers the window where a backup is still in air.
              if (prepared.extracted && imgData.itemId) {
                r.rawTotal += 1;
                r.rawPending += 1;
                rawQueue.push({ url: `${API_BASE_URL}/api/upload-jobs/${jobId}/items/${imgData.itemId}/raw-upload`, file: validFiles[i], attempt: 0 });
                patchRawStatus();
                pumpRawQueue();
              }
            }
          } catch (err) {
            // best-effort; finalize below reconciles the actual uploaded count
          } finally {
            completedCount += 1;
            patchUpload(key, { progress: Math.round((completedCount / validFiles.length) * 50) });
          }
        }
      };

      await Promise.all(Array.from({ length: workerCount }, () => uploadWorker()));

      if (r.cancelled) return;

      await fetch(`${API_BASE_URL}/api/upload-jobs/${jobId}/finalize`, { method: "PATCH", headers: authHeader(key) });

      patchUpload(key, {
        statusText: `Stage 2/2 • AI face scan in progress... ${uploadedCount} uploaded` + quotaSuffix(quotaBaseline, quotaMax, uploadedCount),
        progress: 50,
      });

      pollJobStatus(key, jobId);
    } catch (err) {
      patchUpload(key, { error: `Upload failed: ${err.message}`, isUploading: false, progress: 0, statusText: "" });
    }
  }, [patchUpload, pollJobStatus]);

  const pauseResume = useCallback((eventId, folderId) => {
    const key = makeUploadKey(eventId, folderId);
    const r = getRefs(key);
    const newPaused = !r.paused;
    r.paused = newPaused;
    patchUpload(key, (prev) => ({ isPaused: newPaused, statusText: newPaused ? `Paused — ${prev.statusText || ""}` : prev.statusText }));
  }, [patchUpload]);

  const cancelUpload = useCallback(async (eventId, folderId) => {
    const key = makeUploadKey(eventId, folderId);
    const r = getRefs(key);
    r.cancelled = true;
    r.paused = false;
    if (r.pollTimer) {
      clearTimeout(r.pollTimer);
      r.pollTimer = null;
    }

    const jobId = uploads[key]?.jobId;
    if (jobId) {
      try {
        await fetch(`${API_BASE_URL}/api/upload-jobs/${jobId}/cancel`, { method: "POST", headers: authHeader(key) });
      } catch (e) { /* ignore */ }
    }

    clearUploadFiles(key).catch(() => {});
    localStorage.removeItem(`upload_job_${key}`);
      clearUploadAuth(key);
    r.uploadingFresh = false;

    patchUpload(key, { isUploading: false, jobId: null, progress: 0, statusText: "", isPaused: false, success: "Upload cancelled." });
    setTimeout(() => removeUpload(key), 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploads, patchUpload, removeUpload]);

  const retryFailed = useCallback(async (eventId, folderId) => {
    const key = makeUploadKey(eventId, folderId);
    const jobId = uploads[key]?.lastFinishedJobId;
    if (!jobId) return;

    const r = getRefs(key);
    r.uploadingFresh = false;
    patchUpload(key, { error: "", success: "", isUploading: true, progress: 0, statusText: "Retrying failed uploads..." });

    try {
      const response = await fetch(`${API_BASE_URL}/api/upload-jobs/${jobId}/retry-failed`, { method: "POST", headers: authHeader(key) });
      if (!response.ok) throw new Error("Retry request failed");
      const data = await response.json();
      if (data.retriedCount === 0) {
        patchUpload(key, { isUploading: false, statusText: "", success: "No failed items to retry." });
        setTimeout(() => removeUpload(key), 2000);
        return;
      }
      patchUpload(key, { jobId, failedCount: 0, lastFinishedJobId: null });
      localStorage.setItem(`upload_job_${key}`, jobId.toString());
      pollJobStatus(key, jobId);
    } catch (err) {
      patchUpload(key, { isUploading: false, statusText: "", error: "Failed to retry. Please try again." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploads, patchUpload, pollJobStatus, removeUpload]);

  const value = {
    uploads,
    startUpload,
    pauseResume,
    cancelUpload,
    retryFailed,
    dismissUpload,
  };

  return (
    <UploadManagerContext.Provider value={value}>
      {children}
    </UploadManagerContext.Provider>
  );
}
